import React, { useContext } from 'react';
import { Typography, Button, Skeleton } from 'antd';
import { IconPlus, IconChevronRight, IconHeart, IconHeartFilled, IconBuildingStore } from "@tabler/icons-react";
import SafeImage from './SafeImage';
import { renderSafeText, formatUnit, capitalizeFirst, toTitleCase, formatDeliveryTime } from "../util/unitFormatter";
import { WishlistContext } from '../WishlistContext';
import { useDeliveryEstimateContext } from '../context/DeliveryEstimateContext';

const { Text, Title } = Typography;

const RelatedProducts = ({ products, loading, onAddToCart, title }) => {
    const { toggleWishlist, isInWishlist } = useContext(WishlistContext);
    const { registerShop, formatETA } = useDeliveryEstimateContext();

    if (!loading && (!products || products.length === 0)) return null;

    const renderVariants = (product) => {
        let variants = [];
        if (product.options && product.options.length > 0) {
            product.options.forEach(opt => {
                if (opt.variants) variants.push(...opt.variants);
            });
        } else if (product.hasVariants && product.variants) {
            variants = product.variants;
        }

        if (variants.length === 0) return null;

        return (
            <div className="related-variant-pills">
                <div className="related-variant-pill active">
                    {formatUnit(variants[0].val || variants[0].value, product.variantType || "Option")}
                </div>
                {variants.length > 1 && (
                    <div className="related-variant-pill">
                        +{variants.length - 1} more
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="related-products-section">
            <div className="related-products-header">
                <Title level={5} className="related-title">{title || "You May Also Like"}</Title>
            </div>

            <div className="related-products-scroll">
                {loading ? (
                    [1, 2, 3].map((i) => (
                        <div key={i} className="related-product-card skeleton">
                            <Skeleton.Button active style={{ width: 140, height: 140, borderRadius: 12 }} />
                            <Skeleton active paragraph={{ rows: 2 }} size="small" />
                        </div>
                    ))
                ) : (
                    products.map((product) => {
                        const isWishlisted = isInWishlist(product._id);
                        const shopId = product.shopId?._id || product.shopId;
                        const shopName = product.shopId?.name || product.shopName || "Nearzon Shop";
                        const fallbackTime = product.estimatedDeliveryTime || (product.shopId?.business?.deliveryTime || "30-45 mins");

                        // Register for dynamic ETA estimation
                        if (shopId && registerShop) registerShop(shopId);
                        const deliveryTime = formatETA(shopId, fallbackTime);

                        return (
                            <div key={product._id} className="related-product-card-premium">
                                <div className="related-image-container">
                                    <div className="related-image-wrapper" onClick={() => window.open(`/single/Products/${product._id}`, '_blank')}>
                                        <SafeImage
                                            src={product.imageUrl || (product.images && product.images[0])}
                                            alt={product.name}
                                            variant="square"
                                            className="related-product-img"
                                        />
                                    </div>

                                    <div className={`related-wishlist-icon ${isWishlisted ? 'active' : ''}`} onClick={() => toggleWishlist(product._id)}>
                                        {isWishlisted ? <IconHeartFilled size={14} /> : <IconHeart size={14} />}
                                    </div>

                                    <Button
                                        type="primary"
                                        size="small"
                                        className="related-add-badge-btn"
                                        onClick={() => onAddToCart(product)}
                                    >
                                        ADD
                                    </Button>
                                </div>

                                <div className="related-content">
                                    <Text className="related-product-name-premium" onClick={() => window.open(`/single/Products/${product._id}`, '_blank')}>
                                        {capitalizeFirst(product.name)}
                                    </Text>
                                    
                                    <div className="related-delivery-text">
                                        Delivery By {deliveryTime}
                                    </div>

                                    <div className="related-price-row-premium">
                                        <span className="related-price-premium">₹{product.price}</span>
                                        {product.mrp > product.price && (
                                            <span className="related-mrp-premium">MRP ₹{product.mrp}</span>
                                        )}
                                    </div>

                                    {renderVariants(product)}
                                </div>

                                <div className="related-shop-strip">
                                    <IconBuildingStore size={12} className="related-shop-icon" />
                                    <span className="related-shop-name-text">{toTitleCase(shopName)}</span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default RelatedProducts;
